import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Crown, TrendingUp, Shield, Zap, CheckCircle } from 'lucide-react';
import ContactForm from '../contact/ContactForm';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  featureName?: string;
  limitType?: 'properties' | 'users' | 'rentalUnits' | 'feature';
  currentLimit?: number;
  currentUsage?: number;
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  featureName,
  limitType,
  currentLimit,
  currentUsage
}) => {
  const [showSuccess, setShowSuccess] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      // Reset success state when modal opens
      setShowSuccess(false);
      setCountdown(3);
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  useEffect(() => {
    if (showSuccess && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (showSuccess && countdown === 0) {
      onClose();
    }
  }, [showSuccess, countdown, onClose]);

  if (!isOpen) return null;

  const getLimitMessage = () => {
    switch (limitType) {
      case 'properties':
        return `You've reached your limit of ${currentLimit} properties`;
      case 'users':
        return `You've reached your limit of ${currentLimit} users`;
      case 'rentalUnits':
        return `You've reached your limit of ${currentLimit} rental units`;
      case 'feature':
        return `The feature "${featureName}" is not available in your current plan`;
      default:
        return 'This feature requires an upgraded plan';
    }
  };

  const getPrefillMessage = () => {
    let message = "Hi, I'm interested in upgrading my subscription plan.\n\n";

    switch (limitType) {
      case 'properties':
        message += `I've reached my current limit of ${currentLimit} properties (currently using ${currentUsage}/${currentLimit}). I need to add more properties to continue growing my business.`;
        break;
      case 'users':
        message += `I've reached my current limit of ${currentLimit} users (currently using ${currentUsage}/${currentLimit}). I need to add more team members to manage operations effectively.`;
        break;
      case 'rentalUnits':
        message += `I've reached my current limit of ${currentLimit} rental units (currently using ${currentUsage}/${currentLimit}). I need to manage more rental units as my portfolio expands.`;
        break;
      case 'feature':
        message += `I need access to the "${featureName}" feature which is not available in my current plan. This feature is important for my business needs.`;
        break;
      default:
        message += `I'd like to learn more about upgrading to access additional features and capabilities.`;
    }

    message += "\n\nPlease help me choose the right plan for my requirements.";
    return message;
  };

  const benefits = [
    { icon: TrendingUp, title: 'Unlimited Growth', description: 'Scale your business without restrictions' },
    { icon: Shield, title: 'Premium Features', description: 'Access advanced tools and analytics' },
    { icon: Zap, title: 'Priority Support', description: '24/7 dedicated customer support' },
    { icon: Crown, title: 'Enhanced Security', description: 'Enterprise-grade security features' }
  ];


  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] overflow-y-auto" style={{ minWidth: '100vw' }}>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        style={{ minWidth: '100vw', minHeight: '100vh' }}
        onClick={onClose}
      />

      {/* Modal Content */}
      <div className="fixed inset-0 flex items-center justify-center p-4" style={{ minWidth: '100vw', minHeight: '100vh' }}>
        <div className="relative bg-white dark:bg-gray-800 w-full max-w-6xl min-w-[90vw] md:min-w-[80vw] lg:min-w-[1200px] max-h-[90vh] rounded-2xl shadow-2xl transform transition-all overflow-hidden flex flex-col">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-6 right-6 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors z-10"
          >
            <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
          </button>

          {/* Header Section - Fixed */}
          <div className="relative overflow-hidden bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 p-8 rounded-t-2xl flex-shrink-0">
            {/* Pattern overlay */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{
                backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)`,
              }}></div>
            </div>

            <div className="relative">
              <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur rounded-2xl mb-4">
                <Crown className="h-8 w-8 text-white" />
              </div>

              <h2 className="text-3xl font-bold text-white mb-2">
                Unlock Your Full Potential
              </h2>

              <p className="text-lg text-purple-100 max-w-2xl">
                {getLimitMessage()}
              </p>

              {currentUsage !== undefined && currentLimit !== undefined && currentLimit > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-purple-100 text-sm mb-2">
                    <span>Current Usage</span>
                    <span>{currentUsage} / {currentLimit}</span>
                  </div>
                  <div className="w-full bg-white/20 rounded-full h-2">
                    <div
                      className="bg-white h-2 rounded-full transition-all"
                      style={{ width: `${(currentUsage / currentLimit) * 100}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {showSuccess ? (
              /* Success State */
              <div className="flex flex-col items-center justify-center p-12 min-h-[400px]">
                {/* Animated Checkmark */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping" style={{ animationDuration: '1.5s' }}></div>
                  <div className="relative w-24 h-24 bg-green-500 rounded-full flex items-center justify-center animate-scale-in">
                    <CheckCircle className="h-16 w-16 text-white" strokeWidth={2.5} />
                  </div>
                </div>

                {/* Success Message */}
                <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3 animate-fade-in">
                  Message Received!
                </h3>
                <p className="text-lg text-gray-600 dark:text-gray-400 text-center mb-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
                  Thank you for reaching out. We'll get back to you soon.
                </p>

                {/* Countdown */}
                <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 animate-fade-in" style={{ animationDelay: '0.4s' }}>
                  <span>Closing in</span>
                  <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 font-bold">
                    {countdown}
                  </span>
                  <span>seconds...</span>
                </div>
              </div>
            ) : (
              <>
                {/* Benefits Section */}
                <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                    Why Upgrade?
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {benefits.map((benefit, index) => (
                      <div key={index} className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center mb-2">
                          <benefit.icon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white text-sm mb-1">
                          {benefit.title}
                        </h4>
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          {benefit.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Form Section */}
                <div className="p-8">
                  <ContactForm
                    heading="Let's Upgrade Your Plan"
                    subheading="Fill out the form below and our team will contact you within 24 hours to help you choose the perfect plan."
                    showHeader={true}
                    boxed={false}
                    prefill={{
                      subject: "subscription",
                      message: getPrefillMessage()
                    }}
                    onSubmitted={() => {
                      setShowSuccess(true);
                    }}
                    className="max-w-4xl mx-auto"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default UpgradeModal;